/*
 * 选中悬浮菜单
 *
 * 用户在文档预览里选中文字 / 点单元格后,在选区上方弹出浮动菜单。
 * 支持「加入到会话」主按钮 + 多个 quick action(会预填 prompt 进输入框)。
 *
 * 实现:自定义 fixed div(不用 antd Popover —— 之前接 antd 时 portal'd content 的
 * button click 在 React 16 + rc-trigger 下会被吞,docx 多图换图弹不出 picker)。
 *   - placement(top/right/bottom/left)由父组件按预览容器边界算好传入
 *   - 箭头水平/垂直位置:对齐 cell 中心,clamp 到浮窗内侧 20px 内
 */

import React, { useEffect, useState } from 'react'
import { PencilIcon, SparklesIcon, TagIcon } from './blocks/icons'
import './SelectionMenu.scss'

// localStorage key:用户是否已经看过浮窗
const FIRST_TIME_LS_KEY = 'aiClerk:selectionMenu:seen'

function readHasSeenFirstTime(): boolean {
  try {
    return window.localStorage.getItem(FIRST_TIME_LS_KEY) === '1'
  } catch {
    return true
  }
}

function markFirstTimeSeen(): void {
  try {
    window.localStorage.setItem(FIRST_TIME_LS_KEY, '1')
  } catch {
    // 写不进去就拉倒
  }
}

/** Quick Action:点击后把 quote 加进 chat 并预填一段 prompt 到输入框 */
export interface QuickAction {
  id: string
  icon: 'sparkles' | 'tag' | 'pencil' | React.ReactNode
  label: string
  prompt: string
  danger?: boolean
}

function renderIcon(icon: QuickAction['icon']): React.ReactNode {
  if (icon === 'sparkles') {
    return <SparklesIcon className="aiClerk-selection-menu__icon-svg" />
  }
  if (icon === 'tag') {
    return <TagIcon className="aiClerk-selection-menu__icon-svg" />
  }
  if (icon === 'pencil') {
    return <PencilIcon className="aiClerk-selection-menu__icon-svg" />
  }
  return icon
}

export type MenuPlacement = 'top' | 'right' | 'bottom' | 'left'

interface SelectionMenuProps {
  /** 选区 viewport rect(getBoundingClientRect 结果,父组件已裁剪到容器可见区) */
  rect: { left: number; top: number; right: number; bottom: number } | null
  /** 浮窗摆放方位:父组件按预览容器边界算好 */
  placement?: MenuPlacement
  onAddToChat: () => void
  onClose: () => void
  label?: string
  actions?: QuickAction[]
  onAction?: (action: QuickAction) => void
}

const GAP = 8 // 浮窗与 cell 的间距

export const SelectionMenu: React.FC<SelectionMenuProps> = ({
  rect,
  placement = 'top',
  onAddToChat,
  onClose,
  label = 'AI 帮填',
  actions,
  onAction
}) => {
  const [isFirstTime, setIsFirstTime] = useState<boolean>(
    () => !readHasSeenFirstTime()
  )

  // 滚动 / Esc 关闭
  useEffect(() => {
    if (!rect) return
    const handleScroll = () => onClose()
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [rect, onClose])

  // 首次出现:写 localStorage 标记 + 把组件状态降级,shine 只放一次
  useEffect(() => {
    if (!rect || !isFirstTime) return
    markFirstTimeSeen()
    const timer = window.setTimeout(() => setIsFirstTime(false), 1700)
    return () => window.clearTimeout(timer)
  }, [rect, isFirstTime])

  if (!rect) return null

  // 浮窗定位 + 箭头偏移:基于 placement + cell rect 算
  //   top/bottom: 浮窗水平居中 cell,垂直贴 cell 上/下
  //   left/right: 浮窗垂直居中 cell,水平贴 cell 左/右
  //   箭头:对齐 cell 中心,clamp 到浮窗内侧
  const cx = (rect.left + rect.right) / 2
  const cy = (rect.top + rect.bottom) / 2
  let menuLeft: number
  let menuTop: number
  let menuTransform: string
  if (placement === 'top') {
    menuLeft = cx
    menuTop = rect.top - GAP
    menuTransform = 'translate(-50%, -100%)'
  } else if (placement === 'bottom') {
    menuLeft = cx
    menuTop = rect.bottom + GAP
    menuTransform = 'translate(-50%, 0)'
  } else if (placement === 'right') {
    menuLeft = rect.right + GAP
    menuTop = cy
    menuTransform = 'translate(0, -50%)'
  } else {
    menuLeft = rect.left - GAP
    menuTop = cy
    menuTransform = 'translate(-100%, -50%)'
  }

  const className = `aiClerk-selection-menu${
    isFirstTime ? ' aiClerk-selection-menu--firsttime' : ''
  } aiClerk-selection-menu--placement-${placement}`

  return (
    <div
      className={className}
      style={{
        position: 'fixed',
        left: menuLeft,
        top: menuTop,
        transform: menuTransform,
        zIndex: 9999
      }}
      onMouseDown={(e) => {
        e.preventDefault() // 防止点击菜单时丢失浏览器文本选区
        e.stopPropagation() // 防止外部 mousedown 监听把 cell range 清掉
      }}
    >
      <span className="aiClerk-selection-menu__arrow" aria-hidden />
      <button
        type="button"
        className="aiClerk-selection-menu__btn aiClerk-selection-menu__btn--primary"
        onClick={onAddToChat}
      >
        <span className="aiClerk-selection-menu__icon">
          <SparklesIcon className="aiClerk-selection-menu__icon-svg" />
        </span>
        {label}
      </button>
      {actions && actions.length > 0 && (
        <div className="aiClerk-selection-menu__actions">
          {actions.map((a) => (
            <button
              key={a.id}
              type="button"
              className={`aiClerk-selection-menu__btn aiClerk-selection-menu__btn--action${
                a.danger ? ' aiClerk-selection-menu__btn--danger' : ''
              }`}
              onClick={() => onAction?.(a)}
              title={a.prompt}
            >
              <span className="aiClerk-selection-menu__icon">
                {renderIcon(a.icon)}
              </span>
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
